<template>
  <main>
    <!-- Loading -->
    <LmComponentLoading v-if="isLoading" fixed />

    <!-- Fixed Components -->
    <div v-if="(canBeEditedByMe || !isOnEditMode)" class="row">
      <div class="wrapper-content w-100">
        <LmTreeSelect
          class="col-3 float-right mt-1 px-0"
          v-model="contentTemplate"
          :options="contentTemplateOptions"
          :label="$t('select_a_content_template')"
          :placeholder="$t('select_a_content_template')"
          @input="onContentTemplateChange"
        />
      </div>
    </div>

    <portal to="documentName">
      <!-- Input del nombre -->
      <LmTextInput
        class="col-12 px-0"
        v-model="form.documentName"
        :v="$v.form.documentName"
        :label="$t('name')"
        :placeholder="$t('name')"
      />
    </portal>

    <!-- Formbuilder Server Components -->
    <LmFormBuilder
      v-if="formbuilderId && (canBeEditedByMe || !isOnEditMode)"
      ref="lmFormbuilder"
      :endpointUriwhereGetComponents="formbuilderPath"
      :endpointUriWhereGetValuesToLoadInEditMode="dataPathOnEditMode"
      :contentTemplateData="contentTemplateData"
      @formObjectToSendToServerChange="updateForm($event)"
    />

    <!-- Vista en modo PDF -->
    <div class="wrapper-content w-100 mb-2">
      <div v-if="!canBeEditedByMe && documentInPdf" class="row">
        <div class="col-sm-12 col-md-12 w-100 mt-4 mb-4">
          <h2 class="mb-4">{{form.documentName}}</h2>
          <a-steps :current="form.documentSteps.position" size="small" direction="horizontal">
            <a-step
              v-for="(item, index) in form.documentSteps.items"
              :key="`step-${index}`"
              :title="item.title" :description="item.description"
            />
          </a-steps>
        </div>
        <div class="mt-2 col-sm-12 col-md-12">
          <iframe id="pdfViewer" :src="documentInPdf.src" width="100%" :height="pdfViewerHeight"/>
        </div>
      </div>

      <LmSigners
        v-if="canBeEditedByMe || !isOnEditMode"
        label="Requiere firma"
        placeholder="Seleccione los usuarios que firmarán el documento"
        v-model="form.signers"
      />
      <hr class="w-100" v-if="(canBeEditedByMe || !isOnEditMode) && isComunicable">
      <LmRecipients
        v-if="(canBeEditedByMe || !isOnEditMode) && isComunicable"
        v-model="form.recipients"
      />
      <hr class="w-100" v-if="canBeEditedByMe || !isOnEditMode">
      <LmShareWith
        v-if="canBeEditedByMe || !isOnEditMode"
        v-model="form.shareWith"
      />
      <hr class="w-100" v-if="!isPublic">
      <LmViewers
        v-if="!isPublic && (canBeEditedByMe || !isOnEditMode)"
        v-model="form.viewers"
      />
    </div>

    <!-- Action Buttons -->
    <div class="wrapper-content w-100">
      <div class="row">
        <LmButton
          :value="$t('cancel')"
          class="col-auto"
          type="button"
          buttonClass="btn-secondary"
          @click="onCancel()"
        />
        <LmButton
          v-if="(isOnEditMode && canBeEditedByMe ) || !isOnEditMode"
          :value="$t('save')"
          class="col-auto"
          type="button"
          buttonClass="btn-primary"
          @click="onSubmit()"
        />
        <LmButton
          v-if="!isOnEditMode"
          :value="$t('send_to_sign')"
          class="col-auto"
          type="button"
          buttonClass="btn-success"
          @click="onSendToSign('create')"
        />
        <LmButton
          v-if="(isOnEditMode && canBeEditedByMe)"
          :value="$t('send_to_sign')"
          class="col-auto"
          type="button"
          buttonClass="btn-success"
          @click="onSendToSign('update')"
        />
        <LmButton
          v-if="isOnEditMode && canBeSignedByMe"
          :value="$t('sign')"
          class="col-auto"
          type="button"
          buttonClass="btn-success"
          @click="onSign()"
        />
        <LmButton
          v-if="isOnEditMode && canBeRejectedByMe"
          :value="$t('reject')"
          class="col-auto"
          type="button"
          buttonClass="btn-danger"
          @click="onReject()"
          requireConfirmation
          confirmationTitle="¿Estas seguro de rechazar el documento?"
          confirmationMessage="El documento se pondrá en edición y se notificará al creador."
        />
        <LmButton
          v-if="canBeEditedByMe && isOnEditMode"
          :value="$t('delete')"
          class="col-auto"
          type="button"
          action="delete"
          buttonClass="btn-danger"
          @click="onDelete()"
        />
        <LmButton
          v-if="canBeEditedByMe || !isOnEditMode"
          :value="$t('preview')"
          class="col-auto"
          type="button"
          buttonClass="btn-warning"
          @click="onPreview()"
        />
        <LmButton
          v-if="(isOnEditMode && canBeEditedByMe ) || !isOnEditMode"
          type="button"
          class="col-auto"
          buttonClass="btn-info"
          :value="$t('save_as_template')"
          @click="$refs.saveContentTemplateModal.show()"
        />
      </div>
    </div>
    <b-modal
      id="lm-create-segment-modal"
      ref="saveContentTemplateModal"
      :title="contentTemplate ? $t('update_template') : $t('create_template_name')"
      :ok-title="contentTemplate ? $t('update_literal') : $t('create')"
      :cancel-title="$t('Cancel')"
      @ok="onSaveAsTemplate"
    >
      <form
        id="lm-create-segment-modal-form"
        novalidate
        class="position-relative"
        @submit.prevent
      >
        <LmTextInput
          v-model="contentTemplateName"
          :label="$t('name')"
          :placeholder="$t('template_reference_name')"
        />
      </form>
    </b-modal>
    <lmConfirmationModal ref="lmConfirmationModalFirmarToken" title="Aviso" message="Va a iniciar el proceso de firma electrónica del documento.  ¿Está seguro?"/>
    <lmConfirmationModal ref="lmValidationModalFirmarToken" title="Aviso" message="Un código fue enviado a su mail, por favor ingreselo a continuación." @ok="setToken"/>
    <b-modal id="anexosModal" :title="previewFile.name" size="xl" hide-footer >
      <iframe :src="previewFile.src" frameborder="0" style="height:75vh;" width="100%"></iframe>
    </b-modal>
  </main>
</template>

<script>
import LmButton from '@/components/form/elements/LmButton'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmFormBuilder from '@/components/LmFormBuilder'
import LmSigners from '@/components/form/elements/LmSigners'
import LmRecipients from '@/components/form/elements/LmRecipients'
import LmShareWith from '@/components/form/elements/LmShareWith'
import LmViewers from '@/components/form/elements/LmViewers'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts.js'
import { EventBus } from '@/event-bus.js'
import lmConfirmationModal from '@/components/modals/LmConfirmationModal'

import {
  required
} from 'vuelidate/lib/validators'

export default {
  mixins: [lmToasts],
  components: {
    LmFormBuilder,
    LmButton,
    LmSigners,
    LmRecipients,
    LmShareWith,
    LmTreeSelect,
    LmTextInput,
    lmConfirmationModal,
    LmViewers
  },
  props: {
    isOnEditMode: {
      type: Boolean,
      default: false
    },
    recipientsConfig: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      formbuilderId: '',
      listbuilderId: '',
      documentTypeId: '',
      isComunicable: false,
      isPublish: false,
      isPublic: false,
      canBeEditedByMe: false,
      canBeSignedByMe: false,
      canBeRejectedByMe: false,
      canBeSendToSignByMe: false,
      documentInPdf: undefined,
      initialForm: '',
      form: {
        documentName: '',
        signers: [],
        documentSteps: {
          position: 0,
          items: []
        },
        recipients: {
          users: [],
          areas: [],
          subareas: [],
          mesas: []
        },
        viewers: {
          users: [],
          areas: [],
          subareas: []
        },
        shareWith: []
      },
      contentTemplateData: {},
      contentTemplate: null,
      contentTemplateOptions: [],
      contentTemplateName: '',
      isLoading: false,
      otpToken: '',
      previewFile: {
        name: '',
        src: ''
      }
    }
  },
  validations: {
    form: {
      documentName: {
        required
      }
    }
  },
  computed: {
    formbuilderPath () {
      return `/formbuilder/${this.formbuilderId}`
    },
    dataPathOnEditMode () {
      if (this.isOnEditMode) {
        return `/documents/${this.$route?.params?.id}`
      } else {
        return ''
      }
    },
    contentTemplatePath () {
      return `contenttemplate/${this.documentTypeId}/all`
    },
    stepsDirection: {
      get () {
        return this.form.signers.length > 6 ? 'vertical' : 'horizontal'
      },
      set () {}
    },
    pdfViewerColumns: {
      get () {
        return this.stepsDirection === 'vertical' ? 'col-sm-12 col-md-10' : 'col-12'
      },
      set () {}
    },
    pdfViewerHeight: {
      get () {
        return this.stepsDirection === 'vertical' ? '100%' : '700px'
      },
      set () {}
    }
  },
  methods: {
    async onSubmit () {
      try {
        this.isLoading = true

        // Validaciones
        await this.validateForm(this.$v)
        await this.validateForm(this.$refs.lmFormbuilder.$v)

        const dataToSendToServer = {
          ...this.form,
          documentTypeId: this.documentTypeId
        }
        if (!this.isOnEditMode) {
          // nuevo
          const res = await commonsApi.axiosPostWrapper('/documents/', dataToSendToServer)
          this.lmToastSuccessMessage(`Se creó ${res?.data?.documentName || ' el documento'} correctamente.`, 'Se guardó exitosamente')
        } else {
          // editar
          const res = await commonsApi.axiosPutWrapper(`/documents/${this.$route?.params?.id}`, dataToSendToServer)
          this.lmToastSuccessMessage(`Se editó ${res?.data?.documentName || ' el documento'} correctamente.`, 'Se guardó exitosamente')
        }

        setTimeout(() => {
          this.$router.push('/inbox')
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onPreview () {
      try {
        this.isLoading = true
        const pdfLocation = await commonsApi.axiosPostWrapper('/documents/preview', {
          id: this.$route.params.id,
          documentTypeId: this.documentTypeId,
          ...this.form
        })

        this.previewFile = pdfLocation.data
        await this.$bvModal.show('anexosModal')
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onSendToSign (mode) {
      // Validación
      try {
        await this.validateForm(this.$v)
        await this.validateForm(this.$refs.lmFormbuilder.$v)
      } catch (error) {
        return false
      }
      try {
        this.isLoading = true
        const targetEndpoint = mode === 'create' ? '/documents/createsendtosign' : '/documents/sendtosign'
        await commonsApi.axiosPostWrapper(targetEndpoint, {
          id: this.$route.params.id,
          documentTypeId: this.documentTypeId,
          ...this.form
        })
        this.lmToastSuccessMessage(`Se envió a firmar ${this.form?.documentName || ' el documento'} correctamente.`, 'Enviado a firmar.')
      } catch (error) {
        console.log(error)
      } finally {
        setTimeout(() => {
          this.$router.push('/inbox')
        }, 650)
        this.isLoading = false
      }
    },
    async onSign () {
      try {
        this.isLoading = true
        await this.$refs.lmConfirmationModalFirmarToken.lmShow()
        await commonsApi.axiosPostWrapper('/documents/generateotp', {
          id: this.$route.params.id
        })
        this.$refs.lmValidationModalFirmarToken.setIsCommentProps(true, true, 'Codigo')
        await this.$refs.lmValidationModalFirmarToken.lmShow()
        await commonsApi.axiosPostWrapper(`/documents/signwithotp`, {
          id: this.$route.params.id,
          token: this.otpToken
        })
        this.lmToastSuccessMessage(`Se firmó ${this.form?.documentName || ' el documento'} correctamente.`, 'Documento firmado.')
        setTimeout(() => {
          this.$router.push('/inbox')
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onReject () {
      try {
        this.isLoading = true
        await commonsApi.axiosPostWrapper(`/documents/reject`, {
          id: this.$route.params.id
        })
        this.lmToastSuccessMessage(`Se rechazó ${this.form?.documentName || ' el documento'} correctamente.`, 'Documento rechazado.')
        setTimeout(() => {
          this.$router.push('/inbox')
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onDelete () {
      try {
        this.isLoading = true
        await commonsApi.axiosPutWrapper(`/documents/${this.$route.params.id}`, { deleted: true })
        setTimeout(() => {
          this.$router.push('/inbox')
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    onCancel () {
      this.$router.push('/inbox')
    },
    updateForm (event) {
      this.form = Object.assign({}, this.form, event)
    },
    validateForm (validation) {
      validation.$touch()
      if (validation.$error) {
        this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error')
        throw new Error('validation error')
      } else {
        if (validation.$pending || validation.$error) {
          throw new Error('validation error')
        }
      }
    },
    async loadContentOnEditMode () {
      if (this.isOnEditMode) {
        try {
          this.isLoading = true
          const document = await commonsApi.axiosGetWrapper(`/documents/${this.$route.params.id}`)
          this.form.documentName = document.data.documentName
          this.form.signers = document.data?.signers || []
          this.form.recipients = document.data?.recipients || {}
          if (document.data.documentSteps) {
            this.form.documentSteps = document.data.documentSteps
          }
          this.form.shareWith = document.data?.shareWith || []
          this.form.viewers = document.data?.viewers || {}
          this.canBeEditedByMe = document.data?.canBeEditedByMe || false
          this.canBeSignedByMe = document.data?.canBeSignedByMe || false
          this.canBeRejectedByMe = document.data?.canBeRejectedByMe || false
          this.canBeSendToSignByMe = document.data?.canBeSendToSignByMe || false
          this.documentInPdf = document.data?.documentInPdf || undefined
        } catch (error) {
          console.log(error)
        } finally {
          this.isLoading = false
        }
      }
    },
    // Funcion que guarda el contenido de this.form como content template o actualiza un template ya creado
    async onSaveAsTemplate (bvModalEvt) {
      bvModalEvt.preventDefault()
      try {
        let res = null
        if (!this.contentTemplate?.id) {
          // Creo un nuevo template
          res = await commonsApi.axiosPostWrapper('/contenttemplate', {
            name: this.contentTemplateName,
            ref: this.documentTypeId,
            template: this.form
          })
          this.lmToastSuccessMessage(`Se creó el template ${this.contentTemplateName}.`, `Operación realizada con éxito.`)
        } else {
          // Actualizo un template
          res = await commonsApi.axiosPutWrapper(`/contenttemplate/${this.contentTemplate?.id}`, {
            name: this.contentTemplateName,
            ref: this.documentTypeId,
            template: this.form
          })
          this.lmToastSuccessMessage(`Se actualizó el template ${this.contentTemplateName}.`, `Operación realizada con éxito.`)
        }
        await this.setContentTemplateOptions()
        // Cargo el template creado o actualizado como seleccionado en el super power
        if (res) {
          this.contentTemplate = {
            id: res.data?._id || res.data?.id,
            name: res.data?.name,
            ref: res.data?.ref
          }
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.$refs.saveContentTemplateModal.hide()
      }
    },
    async updateFormContentFromTemplate (value) {
      try {
        const url = `/contenttemplate/${value?.id}`
        const response = await commonsApi.axiosGetWrapper(url)
        this.contentTemplateData = response.data.template

        for (const key in this.form) {
          if (response.data.template.hasOwnProperty(key)) {
            this.form[key] = response.data.template[key]
          }
        }
      } catch (error) {
        console.log(error)
      }
    },
    async setContentTemplateOptions () {
      this.contentTemplateOptions = await commonsApi.getSelectOptions({
        path: this.contentTemplatePath
      })
    },
    setToken (data) {
      this.otpToken = data.comment
    },
    onContentTemplateChange (event) {
      if (event) {
        this.updateFormContentFromTemplate(event)
        this.contentTemplateName = event.name
      } else {
        this.contentTemplateData = {}
        this.form = JSON.parse(this.initialForm)
      }
    }
  },
  async created () {
    try {
      this.initialForm = JSON.stringify(this.form)

      const documentType = await commonsApi.axiosGetWrapper(`/documenttypes/${this.$route.params.docTypeId}`)
      this.formbuilderId = documentType.data.formbuilderId
      this.listbuilderId = documentType.data.listbuilderId
      this.documentTypeId = documentType.data._id
      this.isComunicable = documentType.data.typeComunicable === 'comunicable'
      this.isPublish = documentType.data.publish
      this.isPublic = documentType.data.typeVisibility === 'public'

      if (!this.isPublish) {
        EventBus.$emit('lmShowGlobalToast', {
          toastMessage: 'Debes publicar el Tipo de Documento para poder añadirle Documentos.',
          toastAttributes: {
            title: 'Error',
            toaster: 'b-toaster-bottom-center',
            variant: 'danger',
            solid: true
          }
        })
        this.$router.push('/inbox')
      }
      await this.setContentTemplateOptions()
      await this.loadContentOnEditMode()
    } catch (error) {
      console.log(error)
    }
  }
}
</script>

<style lang="sass" scoped>
  #pdfViewer
    min-height: 500px
</style>
